import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "labelInput", "fieldType", "fieldTypeLabel",
    "preferencesHeader", "listSelectGroup", "showInListGroup",
    "assetLinkGroup", "numberGroup", "addressGroup",
    "dateGroup", "requiredGroup", "dropdownGroup",
    "hintGroup", "multipleOptions", "radarGroup",
    "sourcesHeader", "integratorSlugSelector", "integratorSlugGroup",
    "integratorDataPointGroup",
    "radarSelector", "multipleOptionsGroup", "radarDateGroup",
    "removeExpirationsGroup"
  ]

  static values = {
    lists: Array,
    assetTypes: Array,
    mapboxIntegration: Boolean,
    companyStructureName: String,
    expirationWas: Boolean
  }

  connect() {
    this.updateFieldTypeDisplay()
    if (this.integratorSlugSelectorTarget.value) {
      this.fetchDataPoints()
    }
    this.toggleIntegratorSlugGroup()
  }

  toggleExpiration(event) {
    const isChecked = event.target.checked;

    if (this.hasRadarDateGroupTarget) {
      this.radarDateGroupTarget.classList.toggle("hidden", !isChecked);

      if (!isChecked) {
        const radarDateGroupCheck = this.radarDateGroupTarget.querySelector("input[type=checkbox]");
        radarDateGroupCheck.checked = false;
      }
    }

    this.#syncRemovalGroupVisibility()
  }

  #syncRemovalGroupVisibility() {
    if (!this.hasRemoveExpirationsGroupTarget) return

    const isDate = this.fieldTypeTarget.value === "Date"
    const checked = this.dateGroupTarget.querySelector("input[type=checkbox]")?.checked
    const show = isDate && !checked && this.expirationWasValue

    this.removeExpirationsGroupTarget.classList.toggle("hidden", !show)
    if (!show) this.#resetRemovalGroup()
  }

  #resetRemovalGroup() {
    const keepRadio = this.removeExpirationsGroupTarget.querySelector('input[value="false"]');
    if (keepRadio) keepRadio.checked = true;
  }

  changeFieldType(event) {
    const selectedType = event.target.value
    this.fieldTypeTarget.value = selectedType
    this.updateFieldTypeDisplay()
    this.integratorSlugSelectorTarget.value = null
    this.clearIntegratorDataPointSelection()
    this.integratorDataPointGroupTarget.classList.add("hidden")
  }

  get integratorDataPointSelectController() {
    if (!this.hasIntegratorDataPointGroupTarget) return null;
    const el = this.integratorDataPointGroupTarget.querySelector('[data-controller~="select"]');
    return el ? this.application.getControllerForElementAndIdentifier(el, "select") : null;
  }

  clearIntegratorDataPointSelection() {
    const ctrl = this.integratorDataPointSelectController;
    if (!ctrl) return;
    if (ctrl.select) {
      ctrl.select.clear(true);
    }
    ctrl.replaceOptions([], null);
  }

  updateFieldTypeDisplay() {
    const currentType = this.fieldTypeTarget.value

    // Update selected state of field type labels
    this.fieldTypeLabelTargets.forEach(label => {
      const fieldType = label.dataset.fieldType
      label.classList.toggle("selected", fieldType === currentType)
    })

    // Show/hide preference sections based on type
    this.preferencesHeaderTarget.classList.toggle("hidden", currentType === "Heading")

    this.listSelectGroupTarget.classList.toggle("hidden", currentType !== "ListSelect")

    this.multipleOptionsGroupTarget.classList.toggle("hidden", currentType !== "ListSelect")

    this.showInListGroupTarget.classList.toggle("hidden",
      ["Heading", "RichText", "Embed"].includes(currentType))

    this.assetLinkGroupTarget.classList.toggle("hidden",
      !["AssetLink", "AssetTag"].includes(currentType))

    this.numberGroupTarget.classList.toggle("hidden", currentType !== "Number")

    this.addressGroupTarget.classList.toggle("hidden",
      currentType !== "AddressData" || !this.mapboxIntegrationValue)

    this.dateGroupTarget.classList.toggle("hidden", currentType !== "Date")

    this.#syncRemovalGroupVisibility()

    this.requiredGroupTarget.classList.toggle("hidden",
      ["Heading", "CheckBox"].includes(currentType))

    this.dropdownGroupTarget.classList.toggle("hidden", currentType !== "Dropdown")

    this.hintGroupTarget.classList.toggle("hidden", currentType === "Heading")

    if (this.hasIntegratorSlugGroupTarget) {
      this.integratorSlugGroupTarget.classList.toggle("hidden", !["Text", "RichText", "Email", "Phone"].includes(currentType))
    }

    if (this.hasRadarGroupTarget) {
      this.radarGroupTarget.classList.toggle("hidden", !["Text", "RichText"].includes(currentType))
    }

    if(this.hasRadarDateGroupTarget) {
      const dateGroupCheck = this.dateGroupTarget.querySelector("input[type=checkbox]");
      this.radarDateGroupTarget.classList.toggle("hidden", currentType !== "Date" || !dateGroupCheck.checked);
    }

    if (
      ['AssetLink', 'AssetTag', 'ListSelect', 'Dropdown'].includes(currentType) || 
      (this.hasRadarSelectorTarget && ['Text', 'RichText'].includes(currentType) ) ||
      (this.hasIntegratorSlugGroupTarget && ['Text', 'RichText', 'Email', 'Phone'].includes(currentType))
    ){
      this.sourcesHeaderTarget.classList.remove("hidden");
    } else {
      this.sourcesHeaderTarget.classList.add("hidden");
    }
  }

  toggleIntegratorSlugGroup() {
    if (!this.hasRadarSelectorTarget) {
      return;
    }

    if (!this.hasIntegratorSlugGroupTarget) {
      return;
    }

    const radar = this.radarSelectorTarget.value;
    if(radar) {
      this.integratorSlugGroupTarget.classList.add("disabled");
      return
    }
    this.integratorSlugGroupTarget.classList.remove("disabled");
  }

  fetchDataPoints(event) {
    const selectedOption = this.integratorSlugSelectorTarget.selectedOptions[0];
    const integratorId = selectedOption?.dataset.id;
    const fieldType = this.fieldTypeTarget ? this.fieldTypeTarget.value : null;

    // When the user explicitly changes the integration, the previously
    // saved data point comes from a different integration's namespace and
    // is no longer meaningful. Clear it so the user can pick a fresh one
    // from the new integration's data points. On initial connect (no
    // event), keep any saved selection so editing an existing field with
    // an unchanged integration round-trips its data point. Scoping to the
    // integration selector specifically guards against future siblings in
    // the bound `integratorSlugGroup` div firing change events that would
    // otherwise look like an integration change.
    const userChangedIntegration =
      event &&
      event.type === "change" &&
      event.target === this.integratorSlugSelectorTarget;
    if (userChangedIntegration) {
      this.clearIntegratorDataPointSelection();
    }

    if (!integratorId) {
      this.integratorDataPointGroupTarget.classList.add("hidden");
      this.integratorDataPointGroupTarget.classList.remove("required");
      this.clearIntegratorDataPointSelection();
      if (this.hasRadarGroupTarget) {
        this.radarGroupTarget.classList.remove("disabled");
      }
      return;
    }

    if (this.hasRadarGroupTarget) {
      this.radarGroupTarget.classList.add("disabled");
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

    return fetch(`/admin/integrators/${integratorId}/data_fill_fields`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken, // Include CSRF token for Rails
      },
      body: JSON.stringify({ type: fieldType }) // Send `type` as JSON payload
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(data => {

        this.updateDataPointDropdown(data);
      })
      .catch(error => console.error("Error fetching data points:", error));  
  }

  updateDataPointDropdown(data) {
    const ctrl = this.integratorDataPointSelectController;
    if (!ctrl) return;

    const dataPoints = data["fields"] || [];
    const options = dataPoints.map((dp) => ({ id: dp, name: dp }));
    const existing = ctrl.select?.items?.[0] || null;

    ctrl.replaceOptions(options, existing);

    this.integratorDataPointGroupTarget.classList.remove("hidden");
    this.integratorDataPointGroupTarget.classList.add("required");
  }

  updateListId(event) {
    // Handle list selection change if needed
  }

  updateLinkableId(event) {
    // Handle asset type selection change if needed
  }
}
